import React from 'react';
import Link from 'next/link';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from '@/components/ui/pattern-viewer';
import { LazySection } from '@/components/ui/LazySection';

interface ProviderDirectorySectionProps {
  filteredProviders: any[];
  viewMode: 'card' | 'list';
  extractFields: (provider: any) => any;
  technologyOptions: { id: string; name: string }[];
  productTypeOptions: { id: string; name: string }[];
  platformOptions: { id: string; name: string }[];
  selectedTechnologies: string[];
  selectedProductTypes: string[];
  selectedPlatforms: string[];
  selectedCountries: string[];
}

export default function ProviderDirectorySection({
  filteredProviders,
  viewMode,
  extractFields,
  technologyOptions,
  productTypeOptions,
  platformOptions,
  selectedTechnologies,
  selectedProductTypes,
  selectedPlatforms,
  selectedCountries,
}: ProviderDirectorySectionProps) {
  return (
    <LazySection minHeight={300}>
      {filteredProviders.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">No providers found.</div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProviders.map((provider: any, idx: number) => {
            const { logoSrc, name, description, location, slug } = extractFields(provider);
            const techIds = provider['Technologies (new)'] || provider['Technologies'] || provider['Technology'] || provider['Tech'] || [];
            const productTypeIds = provider['Product Types'] || [];
            const platformIds = provider['Platforms'] || [];
            const CardContent = (
              <div className="flex flex-col items-center p-6 rounded-xl shadow-lg bg-white bg-card hover:shadow-xl transition-shadow h-full min-h-[260px] max-w-full">
                {/* Logo */}
                <div className="w-full flex justify-center items-center mb-4 bg-white">
                  {logoSrc ? (
                    <img src={logoSrc} alt={name} className="w-full max-w-full h-24 object-contain" style={{ maxHeight: 96 }} />
                  ) : (
                    <div className="w-full max-w-full h-24 flex items-center justify-center bg-neutral-100">
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="64" height="64" rx="12" fill="#E5E7EB"/>
                        <path d="M20 44V40C20 37.7909 21.7909 36 24 36H40C42.2091 36 44 37.7909 44 40V44" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="32" cy="28" r="6" stroke="#A3A3A3" strokeWidth="2"/>
                      </svg>
                    </div>
                  )}
                </div>
                {/* Name */}
                <div className="w-full flex flex-col items-center text-center">
                  <div className="font-display font-bold text-xl mb-1 mt-1 truncate text-primary-700">{name}</div>
                  {location && <div className="font-sans italic text-secondary-600 text-sm mb-1">{location}</div>}
                  {description && <div className="font-sans text-base text-neutral-700 mb-2 break-words line-clamp-2">{description}</div>}
                </div>
                {/* Pills: Technologies and Product Types */}
                <div className="flex flex-col gap-2 mt-2 w-full">
                  {/* Technologies */}
                  {selectedTechnologies.length > 0 && Array.isArray(techIds) && techIds.filter((tech) => selectedTechnologies.includes(tech)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Technologies:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(techIds.filter((tech) => selectedTechnologies.includes(tech)))).map((tech, i) => {
                          const techId = typeof tech === 'string' ? tech : (tech.id || tech);
                          return (
                            <Pill key={"tech-"+techId} type="technology">
                              {technologyOptions.find(opt => opt.id === techId)?.name || techId}
                            </Pill>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Product Types */}
                  {selectedProductTypes.length > 0 && Array.isArray(productTypeIds) && productTypeIds.filter((type) => selectedProductTypes.includes(type)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Product Types:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(productTypeIds.filter((type) => selectedProductTypes.includes(type)))).map((type, i) => (
                          <Pill key={"type-"+type} type="productType">{productTypeOptions.find(opt => opt.id === type)?.name || type}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Platforms */}
                  {selectedPlatforms.length > 0 && Array.isArray(platformIds) && platformIds.filter((platform) => selectedPlatforms.includes(platform)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Platforms:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(platformIds.filter((platform) => selectedPlatforms.includes(platform)))).map((platform, i) => (
                          <Pill key={"platform-"+platform} type="platform">{platformOptions.find(opt => opt.id === platform)?.name || platform}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            return slug ? (
              <Link key={provider.id || provider.slug || provider.name || idx} href={`/providers/${slug}`} className="block group focus:outline-none" role="link" tabIndex={0} aria-label={`View details for ${name}`}>{CardContent}</Link>
            ) : (
              <div key={provider.id || provider.name || idx}>{CardContent}</div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {filteredProviders.map((provider: any, idx: number) => {
            const { logoSrc, name, description, location, slug } = extractFields(provider);
            const techIds = provider['Technologies (new)'] || provider['Technologies'] || provider['Technology'] || provider['Tech'] || [];
            const productTypeIds = provider['Product Types'] || [];
            const platformIds = provider['Platforms'] || [];
            const CardContent = (
              <div className="flex flex-col p-6 rounded-xl shadow-lg bg-white bg-card hover:shadow-2xl hover:-translate-y-1 transition-all w-full cursor-pointer focus:outline-none">
                <div className="flex items-center gap-4 w-full mb-2">
                  <div className="flex-shrink-0 w-28 h-16 flex items-center justify-center bg-white rounded-lg overflow-hidden">
                    {logoSrc ? (
                      <img src={logoSrc} alt={name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                        <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="64" height="64" rx="12" fill="#E5E7EB"/>
                          <path d="M20 44V40C20 37.7909 21.7909 36 24 36H40C42.2091 36 44 37.7909 44 40V44" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="32" cy="28" r="6" stroke="#A3A3A3" strokeWidth="2"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <span className="font-display font-bold text-xl text-primary-700 truncate">{name}</span>
                    {location && <span className="font-sans italic text-secondary-600 text-sm mt-1 truncate">{location}</span>}
                  </div>
                </div>
                {description && <div className="font-sans text-base text-neutral-700 mt-1 mb-2 break-words line-clamp-2 md:line-clamp-3">{description}</div>}
                <div className="flex flex-col gap-2 mt-1">
                  {/* Technologies */}
                  {selectedTechnologies.length > 0 && Array.isArray(techIds) && techIds.filter((tech) => selectedTechnologies.includes(tech)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Technologies:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(techIds.filter((tech) => selectedTechnologies.includes(tech)))).map((tech, i) => {
                          const techId = typeof tech === 'string' ? tech : (tech.id || tech);
                          return (
                            <Pill key={"tech-"+techId} type="technology">
                              {technologyOptions.find(opt => opt.id === techId)?.name || techId}
                            </Pill>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Product Types */}
                  {selectedProductTypes.length > 0 && Array.isArray(productTypeIds) && productTypeIds.filter((type) => selectedProductTypes.includes(type)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Product Types:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(productTypeIds.filter((type) => selectedProductTypes.includes(type)))).map((type, i) => (
                          <Pill key={"type-"+type} type="productType">{productTypeOptions.find(opt => opt.id === type)?.name || type}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Platforms */}
                  {selectedPlatforms.length > 0 && Array.isArray(platformIds) && platformIds.filter((platform) => selectedPlatforms.includes(platform)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Platforms:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(platformIds.filter((platform) => selectedPlatforms.includes(platform)))).map((platform, i) => (
                          <Pill key={"platform-"+platform} type="platform">{platformOptions.find(opt => opt.id === platform)?.name || platform}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            return slug ? (
              <Link key={provider.id || provider.slug || provider.name || idx} href={`/providers/${slug}`} className="block group focus:outline-none" role="link" tabIndex={0} aria-label={`View details for ${name}`}>{CardContent}</Link>
            ) : (
              <div key={provider.id || provider.name || idx}>{CardContent}</div>
            );
          })}
        </div>
      )}
    </LazySection>
  );
} 